import * as dataCollectionTypes from '../../consts/uam/dataCollectionTypes';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapRunCommandActionTypes from '../../consts/yap/yapRunCommandActionTypes';
import * as yapWebSocketActionTypes from '../../consts/yap/yapWebSocketActionTypes';
import * as yapWebSocketEventTypes from '../../consts/yap/yapWebSocketEventTypes';
import {
    addYapLastDataCollectionTime,
    setYapAutoCleaningInProgress,
    setYapRequestActionTypeData,
    setYapUpdateSettingsInProgress,
} from '../../state/ducks/yapEncrypted';
import {makeSelectIsDeviceReady} from '../../state/selectors/iotDevice';
import {makeSelectIsHolderByAssetId, makeSelectRequestActionTypeData} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import converter from '../../utils/converter';
import appErrorService from '../app/appErrorService';
import {getErrorMapping} from '../mapping/yapMappings';
import yapConnectionService from '../yap/yapConnectionService';
import yapFWUService from '../yap/yapFWUService';
import yapService from '../yap/yapService';
import yapStateService from '../yap/yapStateService';
import * as dataCollectionService from './dataCollectionService';
import uamClientService from './uamClientService';

const proceedMessage = async (message) => {
    const data = converter.stringToJson(message.data) || {};
    const {eventType, request, error, assetId, status, requestId, isSBLMode, events, associatedDevices} = data || {};

    const uamError = getErrorMapping(error);

    switch (eventType) {
        case yapWebSocketEventTypes.CONNECTION_STATUS_CHANGE:
            const {engine} = associatedDevices || {};

            yapConnectionService.proceedConnectionChange(status, assetId, isSBLMode, engine);

            break;
        case yapWebSocketEventTypes.ASYNC_REQUEST:
            const {action, value} = request || {};
            const actionStatus = yapService.proceedAction({
                status,
                requestId,
                assetId,
                isSync: action === yapWebSocketActionTypes.REFRESH_INFO,
            });

            if (!actionStatus && action !== yapWebSocketActionTypes.PROGRESS) return;

            switch (action) {
                case yapWebSocketActionTypes.REFRESH_INFO:
                    await yapConnectionService.proceedRefreshInfo(actionStatus, assetId);
                    break;
                case yapWebSocketActionTypes.RUN_COMMAND:
                    const {actionType} = makeSelectRequestActionTypeData()(getState());

                    switch (actionType) {
                        case yapRunCommandActionTypes.FIND_DEVICE:
                            await proceedFindDevice(actionStatus, new Date());
                            break;
                        case yapRunCommandActionTypes.CLEAN_DEVICE:
                            await proceedCleanDevice(actionStatus, uamError);
                            break;
                        default:
                            break;
                    }
                    break;
                case yapWebSocketActionTypes.DATA_COLLECTION:
                    await proceedSynchronizeDataCollection(actionStatus, assetId);
                    break;
                case yapWebSocketActionTypes.UPDATE_SETTING:
                    updateDeviceSettings(actionStatus, assetId);
                    break;
                case yapWebSocketActionTypes.PROGRESS:
                    yapFWUService.updateFirmwareProgress(assetId, value);
                    break;
                case yapWebSocketActionTypes.FIRMWARE_UPGRADE:
                    await yapFWUService.proceedFWU(actionStatus, assetId, uamError);
                    break;
                case yapWebSocketActionTypes.RESET_DEVICE:
                    await proceedResetDevice(actionStatus, assetId);
                    break;
                default:
                    break;
            }
            break;
        case yapWebSocketEventTypes.DEVICE_EVENT:
            if (!events) return;

            events.forEach((event) => {
                yapStateService.updateDeviceCharacteristics(event);
            });
            break;
        default:
            break;
    }
};

const proceedSynchronizeDataCollection = async (status, assetId) => {
    const state = getState();
    const isDeviceReady = makeSelectIsDeviceReady()(state);
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);
    const dataCollectionType = dataCollectionService.getCurrentDataCollectionType();

    if (status === yapActionStatusTypes.SUCCESS) {
        dataCollectionService.setDataCollectionFlowReceived({isReceived: true, isHolder, dataCollectionType});
        dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);

        //for diagnostic data collection flow should wait for INFERENCE_COMPLETED event
        if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
            return;
        }

        dispatch(addYapLastDataCollectionTime());
    } else if (isDeviceReady) {
        await uamClientService.proceedSynchronizeAllData(isHolder, true, dataCollectionType);
    } else {
        if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
            return;
        }
        dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);
    }
};

const proceedResetDevice = (status, assetId) => {
    const state = getState();
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);

    dispatch(
        setYapRequestActionTypeData({
            isHolder,
            actionType: yapRunCommandActionTypes.RESET_DEVICE,
            newValue: {status},
        })
    );

    dispatch(setYapUpdateSettingsInProgress(false, isHolder));
};

const proceedFindDevice = async (status, startVibrateTS) => {
    dispatch(setYapUpdateSettingsInProgress(false));

    if (status === yapActionStatusTypes.SUCCESS) {
        dispatch(
            setYapRequestActionTypeData({actionType: yapRunCommandActionTypes.FIND_DEVICE, newValue: startVibrateTS})
        );
    } else {
        appErrorService.showDeviceSettingsGlobalError();
    }
};

const proceedCleanDevice = async (status, error) => {
    dispatch(
        setYapRequestActionTypeData({
            actionType: yapRunCommandActionTypes.CLEAN_DEVICE,
            newValue: {status, errorCode: error?.errorCode},
        })
    );
    dispatch(setYapAutoCleaningInProgress(false));
};

const updateDeviceSettings = (status, assetId) => {
    const isHolder = makeSelectIsHolderByAssetId(assetId)(getState());

    if (status === yapActionStatusTypes.FAILED) {
        appErrorService.showDeviceSettingsGlobalError();
    } else {
        yapStateService.updateDeviceSettings({isHolder, isNeedInformUser: true});
    }

    dispatch(setYapUpdateSettingsInProgress(false, isHolder));
};

export default {
    proceedCleanDevice,
    proceedFindDevice,
    proceedMessage,
    proceedResetDevice,
    proceedSynchronizeDataCollection,
    updateDeviceSettings,
};
